'use client';

import { useState } from 'react';

/**
 * Synchronously invokes `onReset` during the render pass when `value` changes.
 *
 * Uses React's "adjust state during render" pattern: when the tracked value
 * differs from its previous snapshot, `onReset` fires **before** the JSX is
 * produced, so downstream code (and effects) never see the stale combination
 * of new value + old state.
 *
 * **Important**: `onReset` runs during render — it must only call `setState`
 * and must not trigger side effects.
 */
export const useResetOnChange = <T>(value: T, onReset: () => void): void => {
  const [prev, setPrev] = useState(value);
  if (value !== prev) {
    setPrev(value);
    // Skip when prev was undefined (e.g. initial selection or just-added method) to avoid resetting downstream state
    if (prev !== undefined) onReset();
  }
};
